import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BatchDeclineSummary } from '../model/batch-decline';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private isSignedAtpModalOpenSubject = new BehaviorSubject<any>({
    open: false,
    projectId: '',
    siteVisitRequired: false,
  });
  isSignedAtpModalOpen = this.isSignedAtpModalOpenSubject.asObservable();

  private isSubmittedAllBidsModalOpenSubject = new BehaviorSubject<boolean>(
    false
  );
  isSubmittedAllBidsModalOpen =
    this.isSubmittedAllBidsModalOpenSubject.asObservable();

  private isSubmittedBidModalOpenSubject = new BehaviorSubject<boolean>(false);
  isSubmittedBidModalOpen = this.isSubmittedBidModalOpenSubject.asObservable();

  private isDeclineModalOpenSubject = new BehaviorSubject<any>({
    siteDetails: {},
    isFixedPrice: false,
    isAtp: false,
    isOpen: false,
  });
  isDeclineModalOpen = this.isDeclineModalOpenSubject.asObservable();
  /**
   * Decline confirmations that arrived while a batch summary was still unread.
   * Two Decline All batches on disjoint products never share a lock, so both
   * can finish before either summary is dismissed, and a single decline can
   * land in the same window. Only a batch summary reports a failed product:
   * `applyBatchDeclineResult` leaves a failed row untouched, so it reloads as
   * an ordinary invitation, indistinguishable from one never attempted. So
   * nothing replaces an unread batch summary — not another batch, and not a
   * single decline's confirmation. The queue grows only with declines the
   * vendor starts, and empties as they are dismissed.
   */
  private queuedDeclineModals: any[] = [];

  // parent document dialog from closing on the dashboard when data is refreshed
  private isDocumentModalOpenSubject = new BehaviorSubject<any>({
    siteId: '',
    isOpen: false,
  });
  isDocumentModalOpen = this.isDocumentModalOpenSubject.asObservable();

  private isSubmitDeliverableModalOpenSubject = new BehaviorSubject<any>({
    siteId: '',
    isOpen: false,
  });
  isSubmitDeliverableModalOpen =
    this.isSubmitDeliverableModalOpenSubject.asObservable();

  // `multiple` rides along with `open` so the page-hosted confirmation can say
  // "Offers Accepted!" for a bulk accept and "Offer Accepted!" for a single
  // one. The page hosting the dialog is not the component that triggered the
  // accept, so the plurality has to travel with the open signal — the same
  // reason `isSignedAtpModalOpen` above carries its own payload.
  private isOfferAcceptedModalOpenSubject = new BehaviorSubject<any>({
    open: false,
    multiple: false,
  });
  isOfferAcceptedModalOpen =
    this.isOfferAcceptedModalOpenSubject.asObservable();

  private isAtpSignAcceptedModalOpenSubject = new BehaviorSubject<boolean>(
    false
  );
  isAtpSignAcceptedModalOpen =
    this.isAtpSignAcceptedModalOpenSubject.asObservable();

  private submissionConfirmationDialogTitleSubject =
    new BehaviorSubject<string>('');
  submissionConfirmationDialogTitle =
    this.submissionConfirmationDialogTitleSubject.asObservable();

  private siteVisitConfirmationModalOpenSubject = new BehaviorSubject<boolean>(
    false
  );
  siteVisitConfirmationModalOpen =
    this.siteVisitConfirmationModalOpenSubject.asObservable();

  toggleOpenSignedAtpModal(projectId: any, siteVisitRequired: boolean) {
    this.isSignedAtpModalOpenSubject.next({
      open: true,
      projectId: projectId,
      siteVisitRequired: siteVisitRequired,
    });
  }

  toggleCloseSignedAtpModal() {
    this.isSignedAtpModalOpenSubject.next({
      open: false,
      projectId: '',
      siteVisitRequired: false,
    });
  }

  toggleOpenSubmittedBidModal() {
    this.isSubmittedBidModalOpenSubject.next(true);
  }

  toggleCloseSubmittedBidModal() {
    this.isSubmittedBidModalOpenSubject.next(false);
  }

  toggleOpenSubmittedAllBidsModal() {
    this.isSubmittedAllBidsModalOpenSubject.next(true);
  }

  toggleCloseSubmittedAllBidsModal() {
    this.isSubmittedAllBidsModalOpenSubject.next(false);
  }

  toggleOpenDeclineSuccessModal(
    siteDetail: any,
    isFixedPrice: boolean,
    isAtp: boolean
  ) {
    this.openOrQueueDeclineModal({
      siteDetail: siteDetail,
      isFixedPrice: isFixedPrice,
      isAtp: isAtp,
      isOpen: true,
    });
  }

  toggleCloseDeclineSuccessModal() {
    const queued = this.queuedDeclineModals.shift();
    if (queued) {
      this.isDeclineModalOpenSubject.next(queued);
      return;
    }
    this.isDeclineModalOpenSubject.next({
      siteDetail: {},
      isFixedPrice: false,
      isAtp: false,
      isOpen: false,
    });
  }

  toggleOpenBatchDeclineModal(batch: BatchDeclineSummary) {
    this.openOrQueueDeclineModal({
      isOpen: true,
      isAtp: false,
      isFixedPrice: false,
      siteDetail: {},
      batch,
    });
  }

  /** Show this confirmation, or queue it behind a batch summary nobody has read yet. */
  private openOrQueueDeclineModal(payload: any) {
    const current = this.isDeclineModalOpenSubject.value;
    if (current?.isOpen && current.batch) {
      this.queuedDeclineModals.push(payload);
      return;
    }
    this.isDeclineModalOpenSubject.next(payload);
  }

  toogleOpenDocumentModal(siteId: string) {
    this.isDocumentModalOpenSubject.next({
      siteId: siteId,
      isOpen: true,
    });
  }

  toogleCloseDocumentModal() {
    this.isDocumentModalOpenSubject.next({
      siteId: '',
      isOpen: false,
    });
  }

  toogleOpenSubmitDeliverableModal(siteId: string) {
    this.isSubmitDeliverableModalOpenSubject.next({
      siteId: siteId,
      isOpen: true,
    });
  }

  toogleCloseSubmitDeliverableModal() {
    this.isSubmitDeliverableModalOpenSubject.next({
      siteId: '',
      isOpen: false,
    });
  }

  // `multiple` defaults to false so the single-accept callers that pass
  // nothing keep confirming in the singular exactly as before.
  toggleOpenOfferAcceptedModal(multiple: boolean = false) {
    this.isOfferAcceptedModalOpenSubject.next({
      open: true,
      multiple: multiple,
    });
  }

  toggleCloseOfferAcceptedModal() {
    this.isOfferAcceptedModalOpenSubject.next({
      open: false,
      multiple: false,
    });
  }

  toggleOpenAtpSignAcceptedModal() {
    this.isAtpSignAcceptedModalOpenSubject.next(true);
  }

  toggleCloseAtpSignAcceptedModal() {
    this.isAtpSignAcceptedModalOpenSubject.next(false);
  }

  updateSubmissionConfirmationDialogTitle(title: string) {
    this.submissionConfirmationDialogTitleSubject.next(title);
  }

  toggleOpenSiteVisitConfirmationModal() {
    this.siteVisitConfirmationModalOpenSubject.next(true);
  }

  toggleCloseSiteVisitConfirmationModal() {
    this.siteVisitConfirmationModalOpenSubject.next(false);
  }
}
